/** @jsxImportSource @emotion/react */
import { FormProvider, useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useSelector } from "react-redux";
import { toastr } from "react-redux-toastr";
import * as authSelectors from "../../auth/authSelectors";
import { Drawer } from "../../../components/Drawer";
import { ButtonAction } from "../../../components/NewButtons";
import { ActionSpinner } from "../../../components/ActionSpinner";
import { StyledForm } from "../../../components/StyledForm";
import { SpinnerBlock } from "../../common/Spinner";
import { TextInput } from "../../../components/forms/inputs/TextInput";
import { useEffect } from "react";
import { css } from "@emotion/react";
import {
  useCreatePackingItem,
  useGetPackingItem,
  useUpdatePackingItem,
} from "../service/packing-items-api";
import { useGetKitchenCategories } from "../../kitchen-categories/service/kitchen-categories-api";
import { SelectInput } from "../../../components/forms/inputs/SelectInput";
import { TextAreaInput } from "../../../components/forms/inputs/TextAreaInput";
import { MutatePackingItemPayload } from "../models/packing-item";
import { getErrorMessage, useHandleFetchError } from "../../../error-handler";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  id?: string;
};

const additionalFormStyles = css`
  gap: 16px;
`;

const validationSchema = yup.object({
  name: yup.string().required("Enter name of the kitchen category to continue"),
  title: yup
    .string()
    .required("Enter title of the kitchen category to continue"),
  description: yup
    .string()
    .required("Enter description of the kitchen category to continue"),
  productKitchenCategoryId: yup
    .string()
    .required("Choose product kitchen category id to continue"),
  receiptSetting: yup.string().required("Choose receipt setting to continue"),
  unit: yup.string().optional(),
});

export const MutatePackingItemDrawer = ({ isOpen, onClose, id }: Props) => {
  const organizationKeyName = useSelector(
    authSelectors.selectedOrganizationKeyName
  );
  const methods = useForm<MutatePackingItemPayload>({
    defaultValues: {
      description: "",
      name: "",
      productKitchenCategoryId: "",
      title: "",
      unit: "",
      receiptSetting: "regular",
    },
    resolver: yupResolver(validationSchema),
  });

  const { data, isLoading, error: getPackingItemError } = useGetPackingItem(
    organizationKeyName,
    id
  );

  const {
    data: kitchenCategories,
    isLoading: isLoadingKitchenCategories,
    error: listKitchenCategoriesError,
  } = useGetKitchenCategories(organizationKeyName);

  const kitchenCategoriesOptions = kitchenCategories
    ?.sort((a, b) => {
      return a.name.localeCompare(b.name);
    })
    .map((category) => ({
      label: category.name,
      value: category.id,
    }));

  const {
    mutateAsync: createPackingItem,
    isLoading: isLoadingCreation,
  } = useCreatePackingItem();

  const {
    mutateAsync: updatePackingItem,
    isLoading: isLoadingUpdate,
  } = useUpdatePackingItem();

  const handleOnClose = () => {
    methods.reset();
    onClose();
  };

  const onSubmit = async (data: MutatePackingItemPayload) => {
    try {
      if (id) {
        await updatePackingItem({
          organizationKeyName,
          data: { id, ...data },
        });
      } else {
        await createPackingItem({
          organizationKeyName,
          data,
        });
      }
      toastr.success(
        "Success",
        `Packing item successfully ${id ? "updated" : "created"}`
      );
      handleOnClose();
    } catch (error: unknown) {
      const message = getErrorMessage(error);
      toastr.error("Error", message);
    }
  };

  useEffect(() => {
    if (isLoading || !data) {
      return;
    }
    methods.reset({
      description: data.description,
      name: data.name,
      productKitchenCategoryId: data.kitchenCategoryId,
      title: data.title,
      unit: data.unit,
      receiptSetting: data.receiptSetting,
    });
  }, [isLoading, data, methods]);

  useHandleFetchError(getPackingItemError);
  useHandleFetchError(listKitchenCategoriesError);

  return (
    <Drawer
      title={`${id ? "Edit" : "Create"} packing item`}
      width={500}
      buttons={
        <ButtonAction
          disabled={isLoadingCreation || isLoadingUpdate}
          onClick={methods.handleSubmit(onSubmit)}
          type="button"
        >
          {isLoadingCreation || isLoadingUpdate ? <ActionSpinner /> : "Save"}
        </ButtonAction>
      }
      open={isOpen}
      onClose={handleOnClose}
    >
      <FormProvider {...methods}>
        {isLoading || isLoadingKitchenCategories ? (
          <SpinnerBlock />
        ) : (
          <StyledForm additionalStyles={additionalFormStyles}>
            <TextInput
              label="Packing item name"
              name="name"
              placeholder="Enter name"
              helperText="Only visible to admins."
            />
            <TextInput
              label="Packing item display name"
              name="title"
              placeholder="Enter title"
              helperText="Visible for staff on receipt."
            />
            <TextAreaInput
              label="Packing item description"
              name="description"
              placeholder="Enter description"
            />
            <SelectInput
              label="Packing item kitchen category id"
              name="productKitchenCategoryId"
              helperText="Choose kitchen category id for the packing item."
              options={[
                { value: "", label: "None" },
                ...(kitchenCategoriesOptions || []),
              ]}
            />
            <SelectInput
              label="Receipt settings"
              name="receiptSetting"
              helperText="Choose if the packing item should appear on the receipt with a large font or a regular font."
              options={[
                { value: "large", label: "Large font" },
                { value: "regular", label: "Regular font" },
              ]}
            />
            <TextInput
              label="Packing item unit"
              name="unit"
              placeholder="Enter unit"
              helperText="This is the unit of the packing item. E.g. ml, g, kg, etc."
            />
          </StyledForm>
        )}
      </FormProvider>
    </Drawer>
  );
};
